import { useEffect, useState, useCallback } from 'react';
import { Search, Trash2, Clock, RefreshCw, Mail, Inbox, CheckSquare, Square, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface InboxRow {
  address: string; provider: string; created_at: number; expires_at: number | null;
  extend_count: number; message_count: number;
}

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'expired', label: 'Expired' },
];

export default function InboxManager() {
  const navigate = useNavigate();
  const [inboxes, setInboxes] = useState<InboxRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const offset = (page - 1) * pageSize;
    const res = await apiFetch(`/api/admin/inboxes?status=${status}&search=${encodeURIComponent(search)}&limit=${pageSize}&offset=${offset}`);
    const data = await res.json();
    setInboxes(data.inboxes || []);
    setTotal(data.total || 0);
    setSelected(new Set()); // clear selection on reload
    setLoading(false);
  }, [status, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, search, pageSize]);

  const expire = async (address: string) => {
    if (!confirm(`Expire inbox "${address}"?`)) return;
    await apiFetch(`/api/admin/inboxes/${encodeURIComponent(address)}/expire`, { method: 'POST' });
    load();
  };
  const remove = async (address: string) => {
    if (!confirm(`Delete inbox "${address}" and all its messages?`)) return;
    await apiFetch(`/api/admin/inboxes/${encodeURIComponent(address)}`, { method: 'DELETE' });
    load();
  };

  const bulkAction = async (action: 'delete' | 'expire') => {
    const addresses = Array.from(selected);
    if (addresses.length === 0) return;
    const verb = action === 'delete' ? 'delete' : 'expire';
    if (!confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${addresses.length} inbox(es)?`)) return;
    setBulkLoading(true);
    await apiFetch('/api/admin/inboxes/bulk', { method: 'POST', body: JSON.stringify({ action, addresses }) });
    setBulkLoading(false);
    load();
  };

  const toggleOne = (addr: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(addr) ? n.delete(addr) : n.add(addr);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === inboxes.length) setSelected(new Set());
    else setSelected(new Set(inboxes.map(r => r.address)));
  };

  const now = Math.floor(Date.now() / 1000);
  const isActive = (row: InboxRow) => !row.expires_at || row.expires_at > now;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const allSelected = inboxes.length > 0 && selected.size === inboxes.length;
  const someSelected = selected.size > 0 && selected.size < inboxes.length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">Inbox Manager</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{total.toLocaleString()} total inboxes</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 dark:text-gray-400">Per page:</label>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
          {STATUS_TABS.map(t => (
            <button key={t.key} onClick={() => setStatus(t.key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${status === t.key ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by address or provider…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-2xl px-4 py-3">
          <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            {selected.size} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => bulkAction('expire')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-xl text-sm font-medium hover:bg-amber-200 disabled:opacity-50 transition"
            >
              <Clock size={13} /> {bulkLoading ? '…' : 'Expire Selected'}
            </button>
            <button
              onClick={() => bulkAction('delete')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-xl text-sm font-medium hover:bg-red-200 disabled:opacity-50 transition"
            >
              <Trash2 size={13} /> {bulkLoading ? '…' : 'Delete Selected'}
            </button>
          </div>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gray-50/80 dark:bg-gray-700/30">
              <tr>
                <th className="p-4 w-10">
                  <button onClick={toggleAll} className="text-gray-400 hover:text-emerald-500 transition">
                    {allSelected ? <CheckSquare size={16} className="text-emerald-500" /> : someSelected ? <CheckSquare size={16} className="text-emerald-300" /> : <Square size={16} />}
                  </button>
                </th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Address</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Provider</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Messages</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Created</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Expires</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin inline mr-2" />Loading…</td></tr>}
              {!loading && inboxes.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">
                  <Inbox size={32} className="mx-auto mb-2 opacity-40" />No inboxes found
                </td></tr>
              )}
              {!loading && inboxes.map((row, i) => {
                const active = isActive(row);
                const isSel = selected.has(row.address);
                return (
                  <tr key={row.address}
                    className={`border-t border-gray-200/60 dark:border-gray-700/60 hover:bg-emerald-50/30 dark:hover:bg-emerald-900/10 transition ${i % 2 !== 0 ? 'bg-gray-50/20 dark:bg-gray-700/10' : ''} ${isSel ? 'bg-emerald-50/50 dark:bg-emerald-900/20' : ''}`}>
                    <td className="p-4">
                      <button onClick={() => toggleOne(row.address)} className="text-gray-400 hover:text-emerald-500 transition">
                        {isSel ? <CheckSquare size={16} className="text-emerald-500" /> : <Square size={16} />}
                      </button>
                    </td>
                    <td className="p-4 font-mono text-xs text-gray-800 dark:text-gray-200 max-w-[180px] truncate" title={row.address}>{row.address}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-400 capitalize text-xs">{row.provider}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        {active ? 'Active' : 'Expired'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs">
                        <Mail size={12} />{row.message_count}
                      </span>
                    </td>
                    <td className="p-4 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">{new Date(row.created_at * 1000).toLocaleString()}</td>
                    <td className="p-4 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">{row.expires_at ? new Date(row.expires_at * 1000).toLocaleString() : '—'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        {/* View messages */}
                        <button onClick={() => navigate(`/inboxes/${encodeURIComponent(row.address)}`)} title="View messages"
                          className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition">
                          <ExternalLink size={14} />
                        </button>
                        {active && (
                          <button onClick={() => expire(row.address)} title="Expire now"
                            className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition">
                            <Clock size={14} />
                          </button>
                        )}
                        <button onClick={() => remove(row.address)} title="Delete permanently"
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400">
          <span>Page <b>{page}</b> of <b>{pages}</b> · {total.toLocaleString()} total</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs">«</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">← Prev</button>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Next →</button>
            <button onClick={() => setPage(pages)} disabled={page === pages} className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs">»</button>
          </div>
        </div>
      )}
    </div>
  );
}
